import { useState } from "react";
import { mockProductSpecs } from "@/mocks/orders";

interface ProductInfoProps {
  referralCode?: string;
}

export default function ProductInfo({ referralCode }: ProductInfoProps) {
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState("Pearl White");
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeTab, setActiveTab] = useState<"specs" | "highlights" | "shipping">("highlights");

  const specs = mockProductSpecs["1"];
  const colors = ["Pearl White", "Midnight Black", "Sage Green"];

  const handleAddToCart = () => {
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2500);
  };

  const referralLink = `${window.location.origin}/product/1${referralCode ? `?ref=${referralCode}` : ""}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
  };

  return (
    <div className="space-y-5">
      {/* Title & Rating */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold text-orange-500 bg-orange-50 px-2.5 py-1 rounded-full">Electronics</span>
          {referralCode && (
            <span className="text-xs font-semibold text-rose-600 bg-rose-50 px-2.5 py-1 rounded-full flex items-center gap-1">
              <i className="ri-star-line" /> Influencer Deal
            </span>
          )}
        </div>
        <h1 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-tight">
          Premium Wireless Earbuds Pro X200
        </h1>
        <div className="flex items-center gap-3 mt-2">
          <div className="flex items-center gap-1">
            {[1,2,3,4,5].map((s) => (
              <i key={s} className={`text-sm ${s <= 4 ? "ri-star-fill text-yellow-400" : "ri-star-half-fill text-yellow-400"}`} />
            ))}
          </div>
          <span className="text-sm font-semibold text-gray-700">4.8</span>
          <span className="text-sm text-gray-400">(2,341 reviews)</span>
          <span className="text-xs text-green-600 font-medium"><i className="ri-checkbox-circle-fill mr-0.5" />In Stock (500+)</span>
        </div>
      </div>

      {/* Price */}
      <div className="flex items-end gap-3">
        <span className="text-3xl font-extrabold text-gray-900">$49.99</span>
        <span className="text-lg text-gray-400 line-through mb-0.5">$89.99</span>
        <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full mb-0.5">44% OFF</span>
      </div>

      {/* Seller */}
      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
        <img
          src="https://public.readdy.ai/ai/img_res/1b7746bb0e14544eee55b1695684c58d.jpg"
          alt="TechVision Factory"
          className="w-10 h-10 rounded-full object-cover"
        />
        <div>
          <p className="text-sm font-semibold text-gray-900">TechVision Factory</p>
          <p className="text-xs text-gray-500">Verified Seller · 98.7% positive feedback</p>
        </div>
        <span className="ml-auto flex items-center gap-1 text-xs text-green-600 font-semibold bg-green-50 px-2.5 py-1 rounded-full">
          <i className="ri-shield-check-fill" /> Verified
        </span>
      </div>

      {/* Color Selection */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-2">Color: <span className="font-normal text-gray-500">{selectedColor}</span></p>
        <div className="flex gap-2">
          {colors.map((c) => (
            <button
              key={c}
              onClick={() => setSelectedColor(c)}
              className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${selectedColor === c ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Quantity */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-2">Quantity</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer">
              <i className="ri-subtract-line" />
            </button>
            <span className="w-12 text-center text-sm font-semibold text-gray-900">{quantity}</span>
            <button onClick={() => setQuantity(Math.min(99, quantity + 1))} className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer">
              <i className="ri-add-line" />
            </button>
          </div>
          <span className="text-sm text-gray-500">Total: <strong className="text-gray-900">${(49.99 * quantity).toFixed(2)}</strong></span>
        </div>
      </div>

      {/* CTA Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleAddToCart}
          className="flex-1 py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
        >
          {addedToCart ? (
            <span><i className="ri-checkbox-circle-fill mr-2" />Added to Cart!</span>
          ) : (
            <span><i className="ri-shopping-cart-line mr-2" />Add to Cart</span>
          )}
        </button>
        <button className="flex-1 py-3.5 border-2 border-orange-500 text-orange-500 font-bold rounded-xl hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap text-sm">
          <i className="ri-flashlight-line mr-2" />Buy Now
        </button>
      </div>

      {/* Shipping Info */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: "ri-truck-line", label: "Free Shipping", sub: "Orders over $35" },
          { icon: "ri-refresh-line", label: "30-Day Returns", sub: "Hassle-free" },
          { icon: "ri-shield-check-line", label: "Secure Payment", sub: "256-bit SSL" },
        ].map((f) => (
          <div key={f.label} className="text-center p-3 bg-gray-50 rounded-xl">
            <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5">
              <i className={`${f.icon} text-orange-500 text-lg`} />
            </div>
            <p className="text-xs font-semibold text-gray-800">{f.label}</p>
            <p className="text-xs text-gray-400">{f.sub}</p>
          </div>
        ))}
      </div>

      {/* Specs Tabs */}
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-100">
          {(["highlights", "specs", "shipping"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-xs font-semibold capitalize transition-colors cursor-pointer ${activeTab === tab ? "bg-orange-50 text-orange-600 border-b-2 border-orange-500" : "text-gray-500 hover:bg-gray-50"}`}
            >
              {tab === "highlights" ? "Highlights" : tab === "specs" ? "Full Specs" : "Shipping"}
            </button>
          ))}
        </div>
        <div className="p-4">
          {activeTab === "highlights" && (
            <ul className="space-y-2">
              {specs.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <i className="ri-checkbox-circle-fill text-green-500 mt-0.5 flex-shrink-0" />
                  {h}
                </li>
              ))}
            </ul>
          )}
          {activeTab === "specs" && (
            <div className="space-y-2">
              {specs.specs.map((s) => (
                <div key={s.label} className="flex justify-between py-1.5 border-b border-gray-50 last:border-0">
                  <span className="text-xs text-gray-500">{s.label}</span>
                  <span className="text-xs font-medium text-gray-800 text-right max-w-[60%]">{s.value}</span>
                </div>
              ))}
            </div>
          )}
          {activeTab === "shipping" && (
            <div className="space-y-3 text-sm text-gray-700">
              <div className="flex items-center gap-2"><i className="ri-truck-line text-orange-500" /><span>Standard: 3-5 business days (Free over $35)</span></div>
              <div className="flex items-center gap-2"><i className="ri-flashlight-line text-orange-500" /><span>Express: 1-2 business days ($9.99)</span></div>
              <div className="flex items-center gap-2"><i className="ri-map-pin-line text-orange-500" /><span>Ships from Austin, TX — USA domestic only</span></div>
              <div className="flex items-center gap-2"><i className="ri-refresh-line text-orange-500" /><span>30-day returns accepted, free return label</span></div>
            </div>
          )}
        </div>
      </div>

      {/* Influencer Referral Link */}
      <div className="bg-gradient-to-r from-rose-50 to-orange-50 border border-rose-100 rounded-xl p-4">
        <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
          <i className="ri-share-line text-rose-500" /> Share & Earn Commission
        </h4>
        <p className="text-xs text-gray-500 mb-3">Share this product with your followers and earn up to 15% commission on every sale!</p>
        <div className="flex gap-2 mb-3">
          <div className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-500 truncate font-mono">
            {referralLink}
          </div>
          <button onClick={handleCopyLink} className="px-3 py-2 bg-rose-500 text-white text-xs font-semibold rounded-lg hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-file-copy-line mr-1" />Copy
          </button>
        </div>
        <div className="flex gap-2">
          {[
            { icon: "ri-tiktok-line", label: "TikTok", color: "bg-gray-900 text-white" },
            { icon: "ri-youtube-line", label: "YouTube", color: "bg-red-500 text-white" },
            { icon: "ri-twitter-x-line", label: "Twitter", color: "bg-gray-800 text-white" },
          ].map((p) => (
            <button key={p.label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap ${p.color}`}>
              <i className={p.icon} />{p.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
